import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState
} from 'react';
import { normalizeKeywordQuery } from '../../../../utils/keywordQuery';
import {
  buildKeywordCloudMatchIndex,
  countKeywordCloudFromIndex,
  fingerprintExceptSelfFilters
} from '../constants/exploreFilterEngine';
import { toggleKeywordValue } from '../constants/pillFilters';
import PillModeToggle from './PillModeToggle';
import KeywordCloudVirtualList from './KeywordCloudVirtualList';

/** Fixed row stride — matches `.filter-checkbox-row` height (24) + list gap (2). */
const KEYWORD_CLOUD_ROW_HEIGHT = 26;
/** Viewport height of the scrollable value list (CSS max-height). */
const KEYWORD_CLOUD_LIST_MAX_HEIGHT = 220;
/** Below this count, render every row (keeps small lists / unit tests simple). */
const KEYWORD_CLOUD_VIRTUALIZE_AFTER = 40;

/**
 * One frequency-ranked checkbox row. Memoised so virtualised window moves do not
 * re-reconcile every label/bar when only scroll offset changes.
 */
const KeywordCloudRow = memo(function KeywordCloudRow({
  name,
  count,
  checked,
  maxCount,
  onToggle
}) {
  return (
    <label className="filter-checkbox-row keyword-cloud-row">
      <input type="checkbox" checked={checked} onChange={() => onToggle(name)} />
      <span className="filter-checkbox-label">{name}</span>
      <span className="keyword-cloud-bar" aria-hidden="true">
        <span style={{ width: `${Math.round((count / maxCount) * 100)}%` }} />
      </span>
      <span className="filter-checkbox-count">{count.toLocaleString('en-US')}</span>
    </label>
  );
});

KeywordCloudRow.propTypes = {
  name: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  checked: PropTypes.bool.isRequired,
  maxCount: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired
};

/**
 * Pin selected rows to the top without a full sort — partition preserves the
 * count order already present in `list`.
 */
function pinSelectedRows(list, selectedNeedles) {
  if (!selectedNeedles?.size) return list;
  const selected = [];
  const rest = [];
  for (const row of list) {
    const needle = normalizeKeywordQuery(row[0]).needle;
    if (selectedNeedles.has(needle)) selected.push(row);
    else rest.push(row);
  }
  return selected.length ? selected.concat(rest) : list;
}

/**
 * Keyword-cloud filter editor — a searchable, frequency-ranked value list with
 * an All/Any match toggle (default Any / OR). Powers Industry / Value chain /
 * Products, the columns that were previously filterable only by clicking pills.
 *
 * Controlled component: it never touches the store directly — it reports the
 * next filter value (or `null` to clear) via `onChange`, so the same editor
 * works inside the draft-based ColumnFilterPopover and the live-commit
 * Add-filter panel.
 *
 * TD-2626 (C): counts are predictive via match-index + mode combine. The
 * expensive bitmask build is deferred until after first paint so Add-filter
 * open stays responsive, then memoised on the except-self base for toggles.
 * Selected rows are pinned; long lists are windowed (TD-2626 D).
 */
const KeywordCloudFilter = observer(
  ({ columnKey, value, onChange, results, columnFilters, activeSpotlights }) => {
    const [query, setQuery] = useState('');
    // Defer search filtering so keystrokes stay responsive on large lists.
    const deferredQuery = useDeferredValue(query);
    const keywords = value?.keywords;
    const mode = value?.mode || 'or';
    // Spotlights mutate the Set in place — fingerprint so useMemo invalidates
    // when chips toggle without replacing the Set reference (TD-2626 review).
    const spotlightKey = activeSpotlights?.size
      ? [...activeSpotlights].sort((a, b) => a.localeCompare(b)).join('\0')
      : '';
    // Keyword selection must NOT rebuild the match index — only other filters.
    const exceptSelfKey = fingerprintExceptSelfFilters(columnFilters || {}, columnKey);

    // Defer O(candidates × tags) index build until after the panel chrome paints.
    // Sync useMemo on open blocked the Add-filter click for ~100ms+ at Hamid scale.
    const [matchIndex, setMatchIndex] = useState(null);
    const [indexReady, setIndexReady] = useState(false);

    useEffect(() => {
      let cancelled = false;
      let raf2 = 0;
      setIndexReady(false);
      // Double rAF: first frame commits panel chrome, second runs the build.
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          const t0 = performance.now();
          const index = buildKeywordCloudMatchIndex({
            results,
            columnFilters: columnFilters || {},
            columnKey,
            activeSpotlights: activeSpotlights || new Set()
          });
          if (cancelled) return;
          setMatchIndex(index);
          setIndexReady(true);
          console.log(
            'INFO - KeywordCloudFilter - deferred index build ms:',
            Math.round(performance.now() - t0),
            'candidates:',
            index.labels.length
          );
        });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
      // exceptSelfKey / spotlightKey capture filter+spotlight identity.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional fingerprints
    }, [results, columnKey, exceptSelfKey, spotlightKey]);

    const ranked = useMemo(
      () => (matchIndex ? countKeywordCloudFromIndex(matchIndex, keywords, mode) : []),
      [matchIndex, keywords, mode]
    );

    // O(1) selection checks for pin + checkbox state (vs scanning the Set per row).
    const selectedNeedles = useMemo(() => {
      if (!keywords?.size) return null;
      const needles = new Set();
      for (const raw of keywords) {
        const { needle } = normalizeKeywordQuery(raw);
        if (needle) needles.add(needle);
      }
      return needles;
    }, [keywords]);

    const q = deferredQuery.trim().toLowerCase();
    // Pin selected rows to the top so a toggle never jumps the checked value
    // into an invisible part of a long list (TD-2626 vanish-on-select).
    const filtered = useMemo(() => {
      const list = q ? ranked.filter(([name]) => name.toLowerCase().includes(q)) : ranked;
      return pinSelectedRows(list, selectedNeedles);
    }, [ranked, q, selectedNeedles]);
    // maxCount from count-sorted ranked (not pinned list) so bar widths stay honest.
    const maxCount = ranked.length ? Math.max(ranked[0][1], 1) : 1;

    const selectedCount = keywords?.size ?? 0;

    // Offer a free-text add when the query isn't an exact value already listed.
    // Use the live query (not deferred) so the affordance tracks what the user typed.
    const liveQ = query.trim().toLowerCase();
    const exactListed =
      liveQ.length > 0 && ranked.some(([name]) => name.toLowerCase() === liveQ);
    const showFreeAdd = liveQ.length > 0 && !exactListed && indexReady;

    const toggle = useCallback(
      (name) => onChange(toggleKeywordValue(value, name)),
      [onChange, value]
    );
    const addFreeText = () => {
      const term = query.trim();
      if (term) onChange(toggleKeywordValue(value, term));
      setQuery('');
    };
    const setMode = (nextMode) => {
      if (keywords?.size) onChange({ keywords, mode: nextMode });
    };

    const renderRow = useCallback(
      (entry) => {
        const [name, count] = entry;
        const checked = Boolean(selectedNeedles?.has(normalizeKeywordQuery(name).needle));
        return (
          <KeywordCloudRow
            key={name}
            name={name}
            count={count}
            checked={checked}
            maxCount={maxCount}
            onToggle={toggle}
          />
        );
      },
      [selectedNeedles, maxCount, toggle]
    );

    // Hoisted out of JSX to avoid a nested ternary (Sonar).
    let listBody;
    if (!indexReady) {
      listBody = (
        <div className="filter-checkbox-list keyword-cloud-list">
          <div className="filter-empty">Loading values…</div>
        </div>
      );
    } else if (filtered.length === 0) {
      listBody = (
        <div className="filter-checkbox-list keyword-cloud-list">
          <div className="filter-empty">
            {liveQ || q ? 'No matching values.' : 'No values in current results.'}
          </div>
        </div>
      );
    } else if (filtered.length > KEYWORD_CLOUD_VIRTUALIZE_AFTER) {
      listBody = (
        <KeywordCloudVirtualList
          items={filtered}
          rowHeight={KEYWORD_CLOUD_ROW_HEIGHT}
          maxHeight={KEYWORD_CLOUD_LIST_MAX_HEIGHT}
          className="filter-checkbox-list keyword-cloud-list keyword-cloud-list--virtual"
          renderRow={renderRow}
        />
      );
    } else {
      listBody = (
        <div className="filter-checkbox-list keyword-cloud-list">{filtered.map(renderRow)}</div>
      );
    }

    return (
      <div className="keyword-cloud-filter">
        <div className="keyword-cloud-topbar">
          <span className="filter-help">Search or pick the most frequent values</span>
          {selectedCount >= 2 && (
            <span className="keyword-cloud-mode">
              <span className="keyword-cloud-mode-label">Match</span>
              <PillModeToggle mode={mode} onSetMode={setMode} />
            </span>
          )}
        </div>
        {selectedCount >= 2 && mode === 'and' && (
          <p className="keyword-cloud-all-hint">
            Values that would return no matches are hidden in All mode.
          </p>
        )}

        <input
          type="text"
          className="keyword-cloud-search"
          placeholder="Search…"
          aria-label={`Search ${columnKey}`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {showFreeAdd && (
          <button type="button" className="keyword-cloud-add" onClick={addFreeText}>
            Add “{query.trim()}”
          </button>
        )}

        {listBody}
      </div>
    );
  }
);

KeywordCloudFilter.propTypes = {
  columnKey: PropTypes.string.isRequired,
  value: PropTypes.shape({
    keywords: PropTypes.instanceOf(Set),
    mode: PropTypes.oneOf(['and', 'or'])
  }),
  onChange: PropTypes.func.isRequired,
  results: PropTypes.array,
  columnFilters: PropTypes.object,
  activeSpotlights: PropTypes.instanceOf(Set)
};

export default KeywordCloudFilter;
